import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import { type MultiValue, Select, type SingleValue } from 'chakra-react-select';
import type { Dispatch, SetStateAction } from 'react';

import type { UserOverrides } from '@revelio/auth';
import { Tab } from '@revelio/data-access';

import type {
  PipelineSelectOption,
  ReportSelectOption,
} from '../../client-form';
import { serializeOptions } from '../../utils/helpers';
import {
  multiSelectStyles,
  type SelectOption,
  selectControlStyles,
} from './impersonationModal.utils';

interface ImpersonationClientOverridesFormProps {
  customOverrides: UserOverrides;
  setCustomOverrides: Dispatch<SetStateAction<UserOverrides>>;
  onReset: () => void;
  clientSelectOptions: SelectOption<string>[];
  selectedClientOption: SelectOption<string> | undefined;
  onSelectClientGroup: (clientName: string | undefined) => void;
  tabSelectOptions: SelectOption<Tab>[];
  selectedTabOptions: SelectOption<Tab>[];
  pipelineOptions: PipelineSelectOption[];
  selectedPipelineOptions: PipelineSelectOption[];
  reportOptions: ReportSelectOption[];
  selectedReportOptions: ReportSelectOption[];
}

export function ImpersonationClientOverridesForm({
  customOverrides,
  setCustomOverrides,
  onReset,
  clientSelectOptions,
  selectedClientOption,
  onSelectClientGroup,
  tabSelectOptions,
  selectedTabOptions,
  pipelineOptions,
  selectedPipelineOptions,
  reportOptions,
  selectedReportOptions,
}: ImpersonationClientOverridesFormProps) {
  const addableReportOptions = reportOptions.filter(
    (option) =>
      !option.preventAdditions ||
      selectedReportOptions.some((selected) => selected.label === option.label)
  );
  const hasReportsTab = customOverrides.tabs?.includes(Tab.Reports) ?? false;
  const hasDataBuilderTab =
    customOverrides.tabs?.includes(Tab.Deliverables) ?? false;
  return (
    <Box>
      <Flex alignItems="center" justifyContent="space-between" mb={3}>
        <Text fontSize="sm" fontWeight="semibold">
          Client Group Settings
        </Text>
        <Button
          size="xs"
          variant="link"
          colorScheme="blue"
          onClick={onReset}
          data-testid="reset-client-overrides-button"
        >
          Reset Client Overrides
        </Button>
      </Flex>
      <VStack spacing={4} align="stretch">
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="semibold">
            Client Group
          </FormLabel>
          <Select<SelectOption<string>, false>
            options={clientSelectOptions}
            value={selectedClientOption ?? null}
            placeholder="Select client group..."
            onChange={(selected: SingleValue<SelectOption<string>>) =>
              onSelectClientGroup(selected?.value)
            }
            isClearable={true}
            data-testid="client-group-select"
            chakraStyles={selectControlStyles}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm" fontWeight="semibold">
            Tabs
          </FormLabel>
          <Select<SelectOption<Tab>, true>
            isMulti
            options={tabSelectOptions}
            value={selectedTabOptions}
            placeholder="Select tabs..."
            onChange={(selected: MultiValue<SelectOption<Tab>>) => {
              const selectedTabs = selected.map((option) => option.value);
              setCustomOverrides((prev) => ({
                ...prev,
                tabs: selectedTabs.length ? selectedTabs : undefined,
              }));
            }}
            isClearable={true}
            chakraStyles={multiSelectStyles}
          />
        </FormControl>

        {hasReportsTab && (
          <>
            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
              Report Builder Configuration
            </Text>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="semibold">
                Report Types
              </FormLabel>
              <Select<ReportSelectOption, true>
                isMulti
                options={addableReportOptions}
                value={selectedReportOptions}
                placeholder="Select report types..."
                onChange={(selected: MultiValue<ReportSelectOption>) => {
                  const { reportTypes } = serializeOptions(
                    selected.map((option) => ({
                      label: option.label,
                      value: option.value,
                    }))
                  );
                  setCustomOverrides((prev) => ({
                    ...prev,
                    reportTypes: reportTypes.length ? reportTypes : undefined,
                  }));
                }}
                isDisabled={!addableReportOptions.length}
                isClearable={
                  !addableReportOptions.some((option) => option.isFixed)
                }
                noOptionsMessage={() => 'No report types'}
                chakraStyles={multiSelectStyles}
              />
            </FormControl>
          </>
        )}

        {hasDataBuilderTab && (
          <>
            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
              Data Builder Configuration
            </Text>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="semibold">
                Subscribed Datasets
              </FormLabel>
              <Select<PipelineSelectOption, true>
                isMulti
                options={pipelineOptions}
                value={selectedPipelineOptions}
                placeholder="Select datasets..."
                onChange={(selected: MultiValue<PipelineSelectOption>) => {
                  const { pipelineTypes, postingSources } = serializeOptions(
                    selected.map((option) => ({
                      label: option.label,
                      value: option.value,
                    }))
                  );
                  setCustomOverrides((prev) => ({
                    ...prev,
                    pipelineTypes: pipelineTypes.length
                      ? pipelineTypes
                      : undefined,
                    postingSources: postingSources.length
                      ? postingSources
                      : undefined,
                  }));
                }}
                isClearable={true}
                noOptionsMessage={() => 'No datasets'}
                chakraStyles={multiSelectStyles}
              />
            </FormControl>
          </>
        )}

        <FormControl id="linkup-postings-toggle">
          <FormLabel fontSize="sm" fontWeight="semibold">
            Website Postings
          </FormLabel>
          <Flex alignItems="center">
            <FormLabel fontSize="sm">Off</FormLabel>
            <Switch
              id="linkup-postings-toggle"
              isChecked={customOverrides.linkupPostings ?? false}
              onChange={(event) =>
                setCustomOverrides((prev) => ({
                  ...prev,
                  linkupPostings: event.target.checked,
                }))
              }
              colorScheme="green"
              data-testid="linkup-postings-toggle"
            />
            <FormLabel fontSize="sm" ml={3}>
              On
            </FormLabel>
          </Flex>
        </FormControl>
      </VStack>
    </Box>
  );
}
